import React, { useState } from 'react';
import type { FilterFieldDef, FilterOperator, FilterPreset } from '../schema';
import type { ActiveFilter } from '../parser';
import { useI18n } from '@/context/I18nContext';

interface RangeFilterEditorProps {
  fieldDef: FilterFieldDef;
  filter?: ActiveFilter;
  onApply: (value: string, operator: FilterOperator, highValue?: string) => void;
  onClose: () => void;
}

const OPERATORS: { op: FilterOperator; labelKey: string; symbol: string }[] = [
  { op: '=', labelKey: 'filter.op.exact', symbol: '=' },
  { op: '>=', labelKey: 'filter.op.atLeast', symbol: '≥' },
  { op: '<=', labelKey: 'filter.op.atMost', symbol: '≤' },
  { op: 'between', labelKey: 'filter.op.between', symbol: '..' },
  { op: '>', labelKey: 'filter.op.greater', symbol: '>' },
  { op: '<', labelKey: 'filter.op.less', symbol: '<' },
];

export function RangeFilterEditor({
  fieldDef,
  filter,
  onApply,
  onClose,
}: RangeFilterEditorProps) {
  const { t } = useI18n();
  const [operator, setOperator] = useState<FilterOperator>(
    filter?.operator || (fieldDef.type === 'range' ? '>=' : '=')
  );
  const [val, setVal] = useState(filter?.value || '');
  const [highVal, setHighVal] = useState(filter?.highValue || '');
  const unitLabel = fieldDef.unit ? t(fieldDef.unit as any) : '';

  const handleApply = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!val.trim()) return;

    if (operator === 'between') {
      if (highVal.trim()) {
        onApply(val.trim(), 'between', highVal.trim());
      } else {
        onApply(val.trim(), '>=');
      }
    } else {
      onApply(val.trim(), operator);
    }
    onClose();
  };

  const handlePresetClick = (preset: FilterPreset) => {
    if (preset.operator) {
      setOperator(preset.operator);
    }
    setVal(preset.value);
    if (preset.highValue) {
      setHighVal(preset.highValue);
      onApply(preset.value, preset.operator || 'between', preset.highValue);
    } else {
      onApply(preset.value, preset.operator || '=');
    }
    onClose();
  };

  return (
    <form onSubmit={handleApply} className="space-y-4">
      {/* Quick Presets */}
      {fieldDef.presets && fieldDef.presets.length > 0 && (
        <div>
          <div className="text-[0.7rem] font-semibold text-text-muted uppercase tracking-wider mb-2">
            {t('filter.presets')}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {fieldDef.presets.map((p) => {
              const isSelected =
                filter?.value === p.value &&
                (p.highValue ? filter?.highValue === p.highValue : true) &&
                (p.operator ? filter?.operator === p.operator : true);
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => handlePresetClick(p)}
                  className={`p-2.5 sm:p-2 rounded-lg text-xs font-medium text-left transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-accent/15 text-accent border border-accent/40 font-semibold'
                      : 'bg-bg-surface border border-border-default text-text-secondary hover:text-text-primary hover:border-border-hover'
                  }`}
                >
                  {t(p.label as any)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Operator Tabs */}
      <div>
        <label className="text-[0.7rem] font-semibold text-text-muted uppercase tracking-wider block mb-1.5">
          {t('filter.condition')}
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 p-1 bg-bg-surface border border-border-default rounded-lg">
          {OPERATORS.map(({ op, labelKey, symbol }) => {
            const isActive = operator === op;
            const opLabel = t(labelKey as any);
            return (
              <button
                key={op}
                type="button"
                onClick={() => setOperator(op)}
                className={`py-1.5 px-2 rounded-md text-xs font-medium transition-colors cursor-pointer flex flex-col sm:flex-row items-center justify-center gap-1 ${
                  isActive
                    ? 'bg-accent text-white shadow-sm'
                    : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
                }`}
                title={opLabel}
              >
                <span className="font-mono font-bold text-xs">{symbol}</span>
                <span className="text-[0.65rem] sm:hidden">{opLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Inputs */}
      {operator === 'between' ? (
        <div className="grid grid-cols-2 gap-3 items-center">
          <div>
            <label className="text-xs text-text-muted block mb-1 font-medium">{t('filter.min')}</label>
            <div className="relative">
              <input
                type="number"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder={t('filter.min')}
                min={fieldDef.min}
                max={fieldDef.max}
                step={fieldDef.step || 1}
                className="w-full px-3 py-2.5 sm:py-1.5 bg-bg-surface border border-border-default rounded-lg text-sm sm:text-xs text-text-primary focus:outline-none focus:border-accent"
              />
              {unitLabel && (
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-text-muted font-mono">
                  {unitLabel}
                </span>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs text-text-muted block mb-1 font-medium">{t('filter.max')}</label>
            <div className="relative">
              <input
                type="number"
                value={highVal}
                onChange={(e) => setHighVal(e.target.value)}
                placeholder={t('filter.max')}
                min={fieldDef.min}
                max={fieldDef.max}
                step={fieldDef.step || 1}
                className="w-full px-3 py-2.5 sm:py-1.5 bg-bg-surface border border-border-default rounded-lg text-sm sm:text-xs text-text-primary focus:outline-none focus:border-accent"
              />
              {unitLabel && (
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-text-muted font-mono">
                  {unitLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-text-muted font-medium">{t('filter.value')}</label>
            {unitLabel && (
              <span className="text-xs text-text-muted font-mono">{unitLabel}</span>
            )}
          </div>
          <input
            type="number"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="0"
            min={fieldDef.min}
            max={fieldDef.max}
            step={fieldDef.step || 1}
            className="w-full px-3 py-2.5 sm:py-1.5 bg-bg-surface border border-border-default rounded-lg text-sm sm:text-xs text-text-primary focus:outline-none focus:border-accent"
          />
        </div>
      )}

      {/* Buttons */}
      <div className="flex justify-end gap-2 pt-3 border-t border-border-subtle">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
        >
          {t('filter.cancel')}
        </button>
        <button
          type="submit"
          className="px-4 py-2 sm:py-1.5 rounded-lg text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer shadow-sm"
        >
          {t('filter.apply')}
        </button>
      </div>
    </form>
  );
}
